import { get, isEmpty } from 'es-toolkit/compat';
import { DateTime } from 'luxon';

import { Nullable } from '@clearverify/types';

import {
  DataDisputeItemStatus,
  Employer,
  InboxItemsListWhere,
  PeoSimpleInfoFragment,
  VerificationRequestStatus,
  VerificationRequestType,
  Verifier,
  VerifierType,
} from 'src/api';
import { isDataDisputeItemStatus } from 'src/utils/dataDispute';
import { isVerificationRequestStatus } from 'src/utils/verificationRequest';

import { StringifiedDateRange } from '../finalForm';

import { INBOX_FILTER_FUNCTION_MAPPING, VERIFICATION_REQUEST_TYPES } from './constants';
import { InboxFilterFormValues } from './types';

export function getInboxFormFilterObject(data: InboxFilterFormValues) {
  return Object.entries(data).reduce<InboxItemsListWhere>(
    (acc, [field, params]) =>
      isInboxFormFilterValueEmpty(params)
        ? acc
        : // biome-ignore lint/performance/noAccumulatingSpread: spread used to merge filter objects
          { ...acc, ...get(INBOX_FILTER_FUNCTION_MAPPING, field)(params) },
    {},
  );
}

export function getDateCreatedFilter(
  dateCreated: StringifiedDateRange | null,
): InboxItemsListWhere {
  if (!dateCreated) return {};
  const [fromDate, toDate] = dateCreated;

  // The picker stores date-only strings; expand them to local-day boundaries so the last
  // selected day is included and the range matches the user's calendar, not UTC midnight.
  return {
    createdAt: {
      gte: fromDate ? (DateTime.fromISO(fromDate).startOf('day').toISO() ?? undefined) : undefined,
      lte: toDate ? (DateTime.fromISO(toDate).endOf('day').toISO() ?? undefined) : undefined,
    },
  };
}

export function getStatusFilter(
  statuses: Array<VerificationRequestStatus | DataDisputeItemStatus>,
): InboxItemsListWhere | undefined {
  const verificationRequestStatuses = statuses.filter(isVerificationRequestStatus);
  const dataDisputeItemStatuses = statuses.filter(isDataDisputeItemStatus);

  const where: InboxItemsListWhere = {};

  if (verificationRequestStatuses.length !== 0) {
    where.verificationRequestStatus = {
      in: verificationRequestStatuses,
    };
  }

  if (dataDisputeItemStatuses.length !== 0) {
    where.dataDisputeItemStatus = {
      in: dataDisputeItemStatuses,
    };
  }

  if (isEmpty(where)) {
    return undefined;
  }

  return where;
}

export function getEmployeeFilter(employeeSearchText: string): InboxItemsListWhere {
  return { employeeSearchText };
}

export function getVerifierFilter(verifiers: Verifier[]): InboxItemsListWhere {
  const verifierIds = verifiers.map(verifiers => verifiers.id);
  return { verifierIds };
}

export function getEmployerFilter(employers: Employer[]): InboxItemsListWhere {
  const employerIds = employers.map(employer => employer.id);
  return { employerIds };
}

export function getVerifierTypeFilter(verifierType: VerifierType): InboxItemsListWhere {
  return {
    verifierType: { equals: verifierType },
  };
}

export function getVerificationRequestTypesFilter(
  verificationRequestTypes: Array<VerificationRequestType>,
): InboxItemsListWhere {
  return {
    verificationRequestType: {
      in: verificationRequestTypes,
    },
  };
}

export function getPeoFilter(peos: PeoSimpleInfoFragment[]): InboxItemsListWhere {
  const peoIds = peos.map(peo => peo.id);
  return { peoIds };
}

export function getVerificationRequestTypes(verifierType: Nullable<VerifierType>) {
  if (verifierType === VerifierType.Government) return VERIFICATION_REQUEST_TYPES;
  return VERIFICATION_REQUEST_TYPES.filter(
    requestType => requestType !== VerificationRequestType.GovernmentalVerification,
  );
}

export const isInboxFormFilterEmpty = (formValues: InboxFilterFormValues) => {
  return Object.values(formValues).every(value => isInboxFormFilterValueEmpty(value));
};

const isInboxFormFilterValueEmpty = (value: unknown) => {
  return !value || (Array.isArray(value) && value.length === 0);
};
